/**
 * PatientForm — cadastro/edição simplificado de paciente
 * Seções: Identificação | Contato | Sobre a Consulta
 */

import React, { useState, useEffect, useRef } from 'react';
import { patientsService, uploadService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { emo } from '../ui/Ico';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://levnutri.com/nutri';

const EMPTY = {
  name: '',
  birth_date: '',
  gender: 'F',
  cpf: '',
  // Contato
  whatsapp: '',
  email: '',
  cidade: '',
  estado: '',
  // Sobre a consulta
  como_conheceu: '',
  tipo_consulta_padrao: 'presencial',
  plano_contratado: '',
  objetivo_principal: 'perda_peso',
  // Campos clínicos mantidos para o sistema
  objective: 'perda_peso',
  activity_level: 'sedentario',
  initial_height: '',
  initial_weight: '',
  target_weight: '',
  food_preferences: '',
  food_restrictions: '',
  allergies: '',
  notes: '',
  has_hypertension: false,
  has_diabetes: false,
  gestante: false,
  lactante: false,
};

function maskCPF(v) {
  return v.replace(/\D/g, '').slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
}

function maskWhatsApp(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2)  return d.replace(/(\d{1,2})/, '($1');
  if (d.length <= 7)  return d.replace(/(\d{2})(\d{1,5})/, '($1) $2');
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{1,4})/, '($1) $2-$3');
  return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
}

function PhotoPicker({ photoUrl, onChange }) {
  const ref = useRef();
  const [preview, setPreview] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    onChange(file);
  };

  const src = preview || (photoUrl ? `${API_BASE}${photoUrl}` : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
      <div
        onClick={() => ref.current?.click()}
        style={{
          width: 96, height: 96, borderRadius: '50%',
          background: src ? 'transparent' : '#f6f0ea',
          border: '2px dashed #d4a574',
          overflow: 'hidden', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: src ? 0 : '2rem',
          position: 'relative',
        }}
        title="Clique para selecionar foto"
      >
        {emo(src
          ? <img src={src} alt="Foto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : '📷')}
      </div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        style={{ fontSize: '0.78rem', color: '#d4a574', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        {src ? 'Trocar foto' : 'Adicionar foto'}
      </button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  );
}

const S = {
  section: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 1px 4px rgba(0,0,0,.06)',
    border: '1px solid #f6f0ea',
  },
  sectionTitle: {
    margin: '0 0 1.25rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#5a3e2b',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid #f6f0ea',
  },
  row: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' },
  group: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.8rem', fontWeight: '600', color: '#5a3e2b', textTransform: 'uppercase', letterSpacing: '.04em' },
  input: {
    padding: '0.55rem 0.75rem',
    border: '1px solid #ebe3da',
    borderRadius: '8px',
    fontSize: '0.9rem',
    color: '#2f2a26',
    outline: 'none',
    background: '#fbf8f5',
    width: '100%',
    boxSizing: 'border-box',
  },
};

function Field({ label, required, children }) {
  return (
    <div style={S.group}>
      <label style={S.label}>{emo(label)}{required && <span style={{ color: '#c46a60' }}> *</span>}</label>
      {children}
    </div>
  );
}

function PatientForm({ patientId, onNavigate, onSuccess }) {
  const { showSuccess, showError } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(!!patientId);

  useEffect(() => {
    if (patientId) {
      patientsService.getById(patientId)
        .then(res => {
          if (res.success) {
            const d = res.data;
            setForm({
              ...EMPTY,
              ...d,
              has_hypertension: !!d.has_hypertension,
              has_diabetes: !!d.has_diabetes,
              gestante: !!Number(d.gestante),
              lactante: !!Number(d.lactante),
            });
          }
        })
        .catch(() => showError('Erro ao carregar dados do paciente'))
        .finally(() => setLoadingPatient(false));
    }
  }, [patientId]); // eslint-disable-line

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    if (name === 'cpf')       v = maskCPF(value);
    if (name === 'whatsapp')  v = maskWhatsApp(value);
    // keep objective in sync with objetivo_principal
    if (name === 'objetivo_principal') {
      setForm(prev => ({ ...prev, objetivo_principal: v, objective: v }));
      return;
    }
    set(name, v);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.birth_date || !form.gender) {
      showError('Preencha os campos obrigatórios: Nome, Data de Nascimento e Sexo.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        objective: form.objetivo_principal || form.objective,
        target_weight:   form.target_weight   || null,
        initial_height:  form.initial_height  || null,
        initial_weight:  form.initial_weight  || null,
        has_hypertension: form.has_hypertension ? 1 : 0,
        has_diabetes:     form.has_diabetes     ? 1 : 0,
        gestante:         form.gender === 'F' && form.gestante ? 1 : 0,
        lactante:         form.gender === 'F' && form.lactante ? 1 : 0,
        email:    form.email    || null,
        notes:    form.notes    || null,
      };

      const res = patientId
        ? await patientsService.update(patientId, payload)
        : await patientsService.create(payload);

      if (!res.success) { showError(res.message || 'Erro ao salvar'); return; }

      const savedId = patientId || res.data?.id;

      // Upload de foto se houver arquivo selecionado
      if (photoFile && savedId) {
        try {
          await uploadService.patientPhoto(savedId, photoFile);
        } catch {
          showError('Paciente salvo, mas houve um erro no upload da foto.');
        }
      }

      showSuccess(res.message || 'Paciente salvo com sucesso!');
      if (onSuccess) onSuccess();
      else onNavigate('patients');

    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao salvar paciente');
    } finally {
      setLoading(false);
    }
  };

  if (loadingPatient) return <div style={{ padding: '2rem', textAlign: 'center' }}>Carregando...</div>;

  return (
    <div style={{ maxWidth: '860px', margin: '0 auto', padding: '1.5rem' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <button
          type="button"
          onClick={() => onNavigate('patients')}
          style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#d4a574' }}
        >
          ← Voltar
        </button>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: '#2f2a26' }}>
          {patientId ? 'Editar Paciente' : 'Novo Paciente'}
        </h1>
      </header>

      <form onSubmit={handleSubmit}>
        {/* ── SEÇÃO 1: Identificação ── */}
        <div style={S.section}>
          <h2 style={S.sectionTitle}>Identificação</h2>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <PhotoPicker photoUrl={form.foto_url} onChange={setPhotoFile} />
            <div style={{ flex: 1, minWidth: '260px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Field label="Nome completo" required>
                <input style={S.input} name="name" value={form.name} onChange={handleChange} placeholder="Nome da paciente" required />
              </Field>
              <div style={S.row}>
                <Field label="Data de Nascimento" required>
                  <input style={S.input} type="date" name="birth_date" value={form.birth_date} onChange={handleChange} required />
                </Field>
                <Field label="Sexo" required>
                  <select style={S.input} name="gender" value={form.gender} onChange={handleChange}>
                    <option value="F">Feminino</option>
                    <option value="M">Masculino</option>
                    <option value="Outro">Outro</option>
                  </select>
                </Field>
                <Field label="CPF">
                  <input style={S.input} name="cpf" value={form.cpf || ''} onChange={handleChange} placeholder="000.000.000-00" />
                </Field>
              </div>
            </div>
          </div>
        </div>

        {/* ── SEÇÃO 2: Contato ── */}
        <div style={S.section}>
          <h2 style={S.sectionTitle}>Contato</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={S.row}>
              <Field label="WhatsApp">
                <input style={S.input} name="whatsapp" value={form.whatsapp || ''} onChange={handleChange} placeholder="(00) 00000-0000" />
              </Field>
              <Field label="E-mail">
                <input style={S.input} type="email" name="email" value={form.email || ''} onChange={handleChange} placeholder="paciente@email.com" />
              </Field>
            </div>
            <div style={S.row}>
              <Field label="Cidade">
                <input style={S.input} name="cidade" value={form.cidade || ''} onChange={handleChange} placeholder="São Paulo" />
              </Field>
              <Field label="Estado">
                <select style={S.input} name="estado" value={form.estado || ''} onChange={handleChange}>
                  <option value="">—</option>
                  {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA',
                    'PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf =>
                    <option key={uf} value={uf}>{uf}</option>
                  )}
                </select>
              </Field>
            </div>
          </div>
        </div>

        {/* ── SEÇÃO 3: Sobre a Consulta ── */}
        <div style={S.section}>
          <h2 style={S.sectionTitle}>Sobre a Consulta</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={S.row}>
              <Field label="Como conheceu">
                <select style={S.input} name="como_conheceu" value={form.como_conheceu || ''} onChange={handleChange}>
                  <option value="">Não informado</option>
                  <option value="instagram">Instagram</option>
                  <option value="indicacao">Indicação</option>
                  <option value="google">Google</option>
                  <option value="facebook">Facebook</option>
                  <option value="clinica">Clínica / Local</option>
                  <option value="outro">Outro</option>
                </select>
              </Field>
              <Field label="Modalidade">
                <select style={S.input} name="tipo_consulta_padrao" value={form.tipo_consulta_padrao || 'presencial'} onChange={handleChange}>
                  <option value="presencial">Presencial</option>
                  <option value="online">Online</option>
                  <option value="hibrido">Híbrido</option>
                </select>
              </Field>
              <Field label="Plano contratado">
                <select style={S.input} name="plano_contratado" value={form.plano_contratado || ''} onChange={handleChange}>
                  <option value="">Não informado</option>
                  <option value="consulta_avulsa">Consulta Avulsa</option>
                  <option value="plano_mensal">Plano Mensal</option>
                  <option value="plano_trimestral">Plano Trimestral</option>
                  <option value="plano_semestral">Plano Semestral</option>
                  <option value="plano_anual">Plano Anual</option>
                </select>
              </Field>
            </div>
            <div style={S.row}>
              <Field label="Objetivo principal">
                <select style={S.input} name="objetivo_principal" value={form.objetivo_principal || 'perda_peso'} onChange={handleChange}>
                  <option value="perda_peso">Perda de Peso</option>
                  <option value="ganho_massa">Ganho de Massa</option>
                  <option value="ganho_peso">Ganho de Peso</option>
                  <option value="manutencao">Manutenção</option>
                  <option value="saude_geral">Saúde Geral</option>
                  <option value="performance">Performance Esportiva</option>
                </select>
              </Field>
              <Field label="Nível de atividade">
                <select style={S.input} name="activity_level" value={form.activity_level} onChange={handleChange}>
                  <option value="sedentario">Sedentário</option>
                  <option value="leve">Leve (1-2x/sem)</option>
                  <option value="moderado">Moderado (3-5x/sem)</option>
                  <option value="intenso">Intenso (6-7x/sem)</option>
                  <option value="atleta">Atleta</option>
                </select>
              </Field>
            </div>

            {/* Medidas iniciais */}
            <div style={S.row}>
              <Field label="Peso inicial (kg)">
                <input style={S.input} type="number" step="0.1" name="initial_weight" value={form.initial_weight || ''} onChange={handleChange} placeholder="Ex: 70.5" />
              </Field>
              <Field label="Altura (cm)">
                <input style={S.input} type="number" step="0.1" name="initial_height" value={form.initial_height || ''} onChange={handleChange} placeholder="Ex: 165" />
              </Field>
              <Field label="Peso meta (kg)">
                <input style={S.input} type="number" step="0.1" name="target_weight" value={form.target_weight || ''} onChange={handleChange} placeholder="Ex: 62" />
              </Field>
            </div>

            {/* Saúde */}
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', cursor: 'pointer', color: '#2f2a26' }}>
                <input type="checkbox" name="has_hypertension" checked={form.has_hypertension} onChange={handleChange} />
                Hipertensão
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', cursor: 'pointer', color: '#2f2a26' }}>
                <input type="checkbox" name="has_diabetes" checked={form.has_diabetes} onChange={handleChange} />
                Diabetes
              </label>
              {form.gender === 'F' && (
                <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', cursor: 'pointer', color: '#2f2a26' }}>
                    <input type="checkbox" name="gestante" checked={form.gestante} onChange={handleChange} />
                    Gestante
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', cursor: 'pointer', color: '#2f2a26' }}>
                    <input type="checkbox" name="lactante" checked={form.lactante} onChange={handleChange} />
                    Amamentando
                  </label>
                </>
              )}
            </div>

            <Field label="Restrições / Alergias">
              <textarea
                style={{ ...S.input, resize: 'vertical' }}
                name="food_restrictions"
                value={form.food_restrictions || ''}
                onChange={handleChange}
                rows={2}
                placeholder="Ex: intolerância à lactose, alergia ao amendoim..."
              />
            </Field>

            <Field label="Observações">
              <textarea
                style={{ ...S.input, resize: 'vertical' }}
                name="notes"
                value={form.notes || ''}
                onChange={handleChange}
                rows={3}
                placeholder="Informações adicionais relevantes..."
              />
            </Field>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.5rem' }}>
          <button
            type="button"
            onClick={() => onNavigate('patients')}
            style={{
              padding: '0.65rem 1.5rem', borderRadius: '8px',
              border: '1px solid #ebe3da', background: '#ffffff',
              color: '#5a3e2b', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600',
            }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '0.65rem 1.75rem', borderRadius: '8px',
              background: loading ? '#ebe3da' : '#d4a574',
              border: 'none', color: '#ffffff',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem', fontWeight: '600',
            }}
          >
            {loading ? 'Salvando...' : 'Salvar Paciente'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PatientForm;
